import { useEffect, useState } from "react";
import {
  Accessibility,
  X,
  Type,
  Contrast,
  BookOpen,
  Underline,
  MousePointer2,
  Pause,
  PauseCircle,
  RotateCcw,
  Languages,
} from "lucide-react";
import { useA11yPrefs } from "@/hooks/useA11yPrefs";

const LANGS: { code: string; label: string }[] = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "pt", label: "Português" },
  { code: "ht", label: "Kreyòl" },
  { code: "zh-CN", label: "中文" },
  { code: "ru", label: "Русский" },
];

const LANG_CODES = LANGS.map((l) => l.code).join(",");

function readCurrentLang(): string {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
  if (!m) return "en";
  const parts = decodeURIComponent(m[1]).split("/");
  return parts[2] || "en";
}

// The googtrans cookie is what makes the chosen language stick across page
// loads and navigations, so we always write/clear it on every domain variant.
function setTranslateCookie(value: string | null) {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = ["", `; domain=${host}`, `; domain=.${host}`];
  if (parts.length > 2) domains.push(`; domain=.${parts.slice(-2).join(".")}`);
  for (const d of domains) {
    if (value) document.cookie = `googtrans=${value}; path=/${d}`;
    else
      document.cookie = `googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${d}`;
  }
}

function ensureTranslateScript() {
  if (document.getElementById("google-translate-script")) return;
  (window as any).googleTranslateElementInit = () => {
    const g = (window as any).google;
    if (!g?.translate?.TranslateElement) return;
    new g.translate.TranslateElement(
      { pageLanguage: "en", autoDisplay: false, includedLanguages: LANG_CODES },
      "google_translate_element",
    );
  };
  const s = document.createElement("script");
  s.id = "google-translate-script";
  s.src =
    "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.defer = true;
  document.body.appendChild(s);
}

export function AccessibilityWidget() {
  const { prefs, update, reset, hydrated } = useA11yPrefs();
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");

  useEffect(() => {
    const current = readCurrentLang();
    setLang(current);
    // Only load the third-party translator when a translation is active.
    if (current !== "en") ensureTranslateScript();
  }, []);

  const chooseLang = (code: string) => {
    setLang(code);
    if (code === "en") {
      setTranslateCookie(null);
      window.location.reload();
      return;
    }
    const select = document.querySelector<HTMLSelectElement>("select.goog-te-combo");
    setTranslateCookie(`/en/${code}`);
    if (select) {
      select.value = code;
      select.dispatchEvent(new Event("change"));
      return;
    }
    window.location.reload();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && (e.key === "a" || e.key === "A")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!hydrated) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Open accessibility menu"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 left-6 z-40 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition hover:bg-primary/90 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
      >
        <Accessibility className="h-6 w-6" />
      </button>

      <div id="google_translate_element" className="hidden" aria-hidden="true" />

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-start sm:items-center sm:justify-start">
          <button
            type="button"
            aria-label="Close accessibility menu"
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Accessibility preferences"
            data-lenis-prevent
            className="relative m-4 max-h-[90vh] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-2xl bg-[#f5efe4] p-6 text-gray-900 shadow-2xl sm:m-6"
          >
            <div className="flex items-start justify-between">
              <h2 className="text-xl font-semibold">Accessibility</h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="rounded-full p-1 hover:bg-black/5"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-600">
              Preferences save on this device. Press{" "}
              <kbd className="rounded border bg-white px-1 py-0.5 text-[10px]">Alt</kbd> +{" "}
              <kbd className="rounded border bg-white px-1 py-0.5 text-[10px]">A</kbd> anytime.
            </p>

            <div className="mt-4">
              <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                <Type className="h-3.5 w-3.5" /> Text size
              </div>
              <div className="grid grid-cols-3 gap-2">
                {(["A", "A+", "A++"] as const).map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => update({ textSize: size })}
                    className={`rounded-lg border py-2.5 text-sm font-semibold transition ${
                      prefs.textSize === size
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-gray-300 bg-white text-gray-800 hover:border-gray-400"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[10px] text-gray-500">
                Need bigger text? Use your browser zoom (Ctrl/Cmd + +).
              </p>
            </div>

            <div className="mt-5 space-y-2">
              <ToggleRow
                icon={<Contrast className="h-5 w-5" />}
                title="High contrast"
                desc="Darker text, stronger borders"
                checked={prefs.highContrast}
                onChange={(v) => update({ highContrast: v })}
              />
              <ToggleRow
                icon={<BookOpen className="h-5 w-5" />}
                title="Readable font"
                desc="Uses a system sans-serif"
                checked={prefs.readableFont}
                onChange={(v) => update({ readableFont: v })}
              />
              <ToggleRow
                icon={<Underline className="h-5 w-5" />}
                title="Underline all links"
                desc=""
                checked={prefs.underlineLinks}
                onChange={(v) => update({ underlineLinks: v })}
              />
              <ToggleRow
                icon={<MousePointer2 className="h-5 w-5" />}
                title="Larger cursor"
                desc=""
                checked={prefs.largerCursor}
                onChange={(v) => update({ largerCursor: v })}
              />
              <ToggleRow
                icon={<Pause className="h-5 w-5" />}
                title="Reduce motion"
                desc="Pauses animations"
                checked={prefs.reduceMotion}
                onChange={(v) => update({ reduceMotion: v })}
              />
              <ToggleRow
                icon={<PauseCircle className="h-5 w-5" />}
                title="Pause videos & animations"
                desc="Stops autoplaying media"
                checked={prefs.pauseMedia}
                onChange={(v) => update({ pauseMedia: v })}
              />
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-gray-300 pt-4 text-xs">
              <button
                type="button"
                onClick={reset}
                className="flex items-center gap-1.5 text-gray-700 hover:text-gray-900"
              >
                <RotateCcw className="h-4 w-4" /> Reset all
              </button>
              <a href="/ada-compliance" className="font-medium text-primary hover:underline">
                Accessibility statement
              </a>
            </div>

            <div className="mt-4">
              <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                <Languages className="h-3.5 w-3.5" /> Translate this page
              </div>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Translate this page">
                {LANGS.map((l) => {
                  const active = lang === l.code;
                  return (
                    <button
                      key={l.code}
                      type="button"
                      lang={l.code}
                      aria-pressed={active}
                      onClick={() => chooseLang(l.code)}
                      className={`notranslate rounded-full border px-3 py-1 text-xs transition ${
                        active
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-gray-300 bg-white text-gray-800 hover:border-gray-400"
                      }`}
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-[10px] text-gray-500">
                Translates in this tab without redirecting to another website.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ToggleRow({
  icon,
  title,
  desc,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onChange(!checked);
        }
      }}
      className="flex cursor-pointer items-center justify-between rounded-xl bg-white/70 p-2.5 select-none"
    >
      <span className="flex items-center gap-3">
        <span className="text-primary">{icon}</span>
        <span>
          <span className="block text-xs font-semibold text-gray-900">{title}</span>
          {desc && <span className="block text-[10px] text-gray-500">{desc}</span>}
        </span>
      </span>
      <span
        role="switch"
        aria-checked={checked}
        aria-label={title}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition ${
          checked ? "bg-primary" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
    </div>
  );
}

export default AccessibilityWidget;
